import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { phProductService } from '../../services/productService';
import { phPhaseService } from '../../services/phaseService';
import { PhProduct, ESTADO_GLOBAL_OPTIONS } from '../../types/InvestmentProduct';
import { PhPhase, FRECUENCIA_PAGO_OPTIONS } from '../../types/Phase';
import PhPhaseForm from './PhPhaseForm';
import ConfirmModal from '../common/ConfirmModal';
import ActionButton from '../common/ActionButton';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import '../users/UserForm.css';
import './PhaseStyles.css';
import { formatCurrency } from '../../utils/format';
import DateInput from '../common/DateInput';

interface PhFormProps {
  product: PhProduct | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const PhForm: React.FC<PhFormProps> = ({ product, onSuccess, onCancel }) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<PhProduct>({
    nombreProducto: '',
    razonSocial: '',
    cifNif: '',
    direccion: '',
    domicilioSocial: '',
    ibanRecaudacion: '',
    bic: '',
    proximaComunicacion: '',
    estadoGlobal: '',
    frecuenciaPago: 'Trimestral',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Phases state
  const [phases, setPhases] = useState<PhPhase[]>([]);
  const [phasesLoading, setPhasesLoading] = useState(false);
  const [showPhaseForm, setShowPhaseForm] = useState(false);
  const [editingPhase, setEditingPhase] = useState<PhPhase | null>(null);
  const [phasesExpanded, setPhasesExpanded] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [phaseToDelete, setPhaseToDelete] = useState<PhPhase | null>(null);

  useEffect(() => {
    if (product) {
      setFormData(product);
      loadPhases();
    }
  }, [product]);

  const loadPhases = async () => {
    if (!product?.id) return;
    setPhasesLoading(true);
    try {
      const response = await phPhaseService.getAll(product.id);
      setPhases(response.data);
    } catch (err) {
      console.error('Error loading phases:', err);
    } finally {
      setPhasesLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (product?.id) {
        await phProductService.update(product.id, formData);
        showToast('Producto PH actualizado correctamente');
      } else {
        await phProductService.create(formData);
        showToast('Producto PH creado correctamente');
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar el producto PH');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPhase = () => {
    setEditingPhase(null);
    setShowPhaseForm(true);
  };

  const handleEditPhase = (phase: PhPhase) => {
    setEditingPhase(phase);
    setShowPhaseForm(true);
  };

  const handleDeletePhase = (phase: PhPhase) => {
    setPhaseToDelete(phase);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!product?.id || !phaseToDelete?.id) return;
    try {
      await phPhaseService.delete(product.id, phaseToDelete.id);
      showToast('Fase eliminada correctamente');
      loadPhases();
    } catch (err) {
      console.error('Error deleting phase:', err);
      showToast('Error al eliminar la fase', 'error');
    } finally {
      setShowDeleteModal(false);
      setPhaseToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setPhaseToDelete(null);
  };

  const handlePhaseSuccess = () => {
    setShowPhaseForm(false);
    setEditingPhase(null);
    loadPhases();
  };

  if (showPhaseForm && product?.id) {
    return (
      <PhPhaseForm
        productId={product.id}
        phase={editingPhase}
        onSuccess={handlePhaseSuccess}
        onCancel={() => setShowPhaseForm(false)}
      />
    );
  }

  return (
    <div className="user-form">
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Eliminar Fase"
        message={`¿Estás seguro de que deseas eliminar la fase "${phaseToDelete?.nombreFase}"?`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type="danger"
      />
      <h3>{product ? 'Editar Producto PH' : 'Crear Producto PH'}</h3>
      <p className="form-subtitle">Renta Fija - Interés Fijo Anual</p>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit}>
        <h4>Identificación</h4>
        <div className="form-row">
          <div className="form-group span-2">
            <label htmlFor="nombreProducto">Nombre del Producto *</label>
            <input
              type="text"
              id="nombreProducto"
              name="nombreProducto"
              value={formData.nombreProducto}
              onChange={handleChange}
              required
              maxLength={200}
            />
          </div>
          <div className="form-group span-1">
            <label htmlFor="cifNif">CIF/NIF *</label>
            <input
              type="text"
              id="cifNif"
              name="cifNif"
              value={formData.cifNif}
              onChange={handleChange}
              required
              maxLength={20}
            />
          </div>
          <div className="form-group span-3">
            <label htmlFor="direccion">Dirección Activo</label>
            <input
              type="text"
              id="direccion"
              name="direccion"
              value={formData.direccion || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group span-3">
            <label htmlFor="razonSocial">Razón Social *</label>
            <input
              type="text"
              id="razonSocial"
              name="razonSocial"
              value={formData.razonSocial}
              onChange={handleChange}
              required
              maxLength={200}
            />
          </div>
          <div className="form-group span-3">
            <label htmlFor="ibanRecaudacion">IBAN Recaudación</label>
            <input
              type="text"
              id="ibanRecaudacion"
              name="ibanRecaudacion"
              value={formData.ibanRecaudacion || ''}
              onChange={handleChange}
              maxLength={34}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group span-2">
            <label htmlFor="bic">BIC / SWIFT</label>
            <input
              type="text"
              id="bic"
              name="bic"
              value={formData.bic || ''}
              onChange={handleChange}
              maxLength={11}
              placeholder="Ej: BSCHESMMXXX"
            />
          </div>
          <div className="form-group span-4">
            <label htmlFor="domicilioSocial">Domicilio Social</label>
            <input
              type="text"
              id="domicilioSocial"
              name="domicilioSocial"
              value={formData.domicilioSocial || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        <h4>Configuración PH</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="estadoGlobal">Estado Global</label>
            <select
              id="estadoGlobal"
              name="estadoGlobal"
              value={formData.estadoGlobal || ''}
              onChange={handleChange}
            >
              <option value="">Seleccionar...</option>
              {ESTADO_GLOBAL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="frecuenciaPago">Frecuencia Pago</label>
            <select
              id="frecuenciaPago"
              name="frecuenciaPago"
              value={formData.frecuenciaPago || 'Trimestral'}
              onChange={handleChange}
            >
              {FRECUENCIA_PAGO_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="proximaComunicacion">Próxima Comunicación</label>
            <DateInput
              id="proximaComunicacion"
              name="proximaComunicacion"
              value={formData.proximaComunicacion || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        {product && (
          <>
            <h4>Capital (Calculado)</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Capital Objetivo Global</label>
                <input type="text" value={formatCurrency(formData.capObjetivoGlobal)} disabled />
              </div>
            </div>

            <h4>Métricas Capital</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Capital Captado</label>
                <input type="text" value={formatCurrency(formData.capitalCaptado)} disabled />
              </div>
              <div className="form-group">
                <label>Capital Devuelto</label>
                <input type="text" value={formatCurrency(formData.capitalDevuelto)} disabled />
              </div>
              <div className="form-group">
                <label>Capital Vivo</label>
                <input type="text" value={formatCurrency(formData.capitalVivo)} disabled />
              </div>
            </div>

            <h4>Métricas Interés</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Interés Previsto</label>
                <input type="text" value={formatCurrency(formData.interesPrevisto)} disabled />
              </div>
              <div className="form-group">
                <label>Interés Pagado</label>
                <input type="text" value={formatCurrency(formData.interesPagado)} disabled />
              </div>
              <div className="form-group">
                <label>% Interés Pagado</label>
                <input type="text" value={formatPercent(formData.porcentajeInteresPagado)} disabled />
              </div>
            </div>

            {/* Phases Section */}
            <div className="phases-section">
              <div className="phases-header" onClick={() => setPhasesExpanded(!phasesExpanded)}>
                <h4>
                  <span className={`expand-icon ${phasesExpanded ? 'expanded' : ''}`}>&#9654;</span>
                  Fases del Producto ({phases.length})
                </h4>
                <ActionButton
                  icon={FaPlus}
                  text="Añadir Fase"
                  onClick={() => handleAddPhase()}
                  variant="primary"
                />
              </div>
              {phasesExpanded && (
                <div className="phases-content">
                  {phasesLoading ? (
                    <p>Cargando fases...</p>
                  ) : phases.length === 0 ? (
                    <p className="no-phases">No hay fases definidas para este producto.</p>
                  ) : (
                    <table className="phases-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Nombre</th>
                          <th>Cap. Objetivo</th>
                          <th>Total Captado</th>
                          <th>Inversores</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {phases.map((phase) => (
                          <tr key={phase.id}>
                            <td>{phase.id}</td>
                            <td>{phase.nombreFase}</td>
                            <td>{formatCurrency(phase.capObjetivoFase)}</td>
                            <td>{formatCurrency(phase.totalCaptado)}</td>
                            <td>{phase.numInversores || 0}</td>
                            <td className="phase-actions">
                              <ActionButton
                                icon={FaEdit}
                                text="Editar Fase"
                                onClick={() => handleEditPhase(phase)}
                                variant="edit"
                              />
                              <ActionButton
                                icon={FaTrash}
                                text="Eliminar Fase"
                                onClick={() => handleDeletePhase(phase)}
                                variant="delete"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary" disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : product ? 'Actualizar' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
};

const formatPercent = (value?: number) => {
  if (value === undefined || value === null) return '-';
  return `${value.toFixed(2)}%`;
};

export default PhForm;
